import type { CSSProperties } from "react";
import type { Attachment } from "@/api/attachment";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import {
  resolveBackgroundOverlayGradient,
  type BackgroundFit,
  type GradientOverlay,
} from "@/lib/cms-puck/block-style";
import { attachmentThumb } from "@/lib/cms-puck/media-utils";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

function mapBackgroundFit(fit: BackgroundFit): CSSProperties["objectFit"] {
  if (fit === "contain") return "contain";
  if (fit === "stretch") return "fill";
  return "cover";
}

/**
 * Aperçu BO du fond vidéo/embed (poster statique — pas de lecteur live).
 * KEEP IN SYNC conceptuellement avec
 * inventozen-frontpublic/src/components/cms-puck/BlockBackgroundMedia.tsx.
 */
export function BlockBackgroundMediaPreview({
  attachment,
  fit,
  overlay,
}: {
  attachment: Attachment;
  fit: BackgroundFit;
  overlay: GradientOverlay;
}) {
  const cmsNew = useCmsNew();
  const theme = usePuckTheme();
  const palette = theme?.colors ?? [];
  const overlayGradient = resolveBackgroundOverlayGradient(palette, overlay);
  const posterUrl = attachmentThumb(attachment);
  const objectFit = mapBackgroundFit(fit);

  return (
    <>
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ zIndex: 0 }}
        aria-hidden
      >
        {posterUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={posterUrl}
            alt={cmsNew("cms.new.auto.alt.d41d8c", "")}
            className="h-full w-full"
            style={{ width: "100%", height: "100%", objectFit }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-slate-200 text-xs text-slate-500">
            {attachment.kind === "embed"
              ? cmsNew("cms.new.preview.backgroundMedia.embed", "Embed")
              : cmsNew("cms.new.preview.backgroundMedia.video", "Vidéo")}
          </div>
        )}
      </div>
      {overlayGradient ? (
        <div
          className="pointer-events-none absolute inset-0"
          style={{ zIndex: 1, background: overlayGradient }}
          aria-hidden
        />
      ) : null}
    </>
  );
}

export function isBackgroundVideoAttachment(
  attachment: Attachment | undefined | null,
): boolean {
  if (!attachment) return false;
  const kind = attachment.kind ?? "image";
  return kind === "video" || kind === "embed";
}
